import { BRAND } from '@verlo/shared/lib/brand';
/**
 * Profit Waterfall — pure SVG, no chart library dependency.
 * Renders 6 bars (Revenue → -COGS → Gross Profit → -Labour → -Other → EBITDA)
 * with connector lines between bars to show the "step down" effect.
 */
const fmtGBP = (n) =>
  new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 0 }).format(n || 0);

const ProfitWaterfall = ({ steps }) => {
  if (!steps?.length) return null;

  // Compute running totals to position bar starts
  let running = 0;
  const computed = steps.map((s) => {
    if (s.kind === 'start' || s.kind === 'subtotal' || s.kind === 'end') {
      // For absolute steps, set running = value
      const item = { ...s, start: 0, end: s.value, running: s.value };
      running = s.value;
      return item;
    }
    // negative deltas step down
    const start = running;
    const end = running + s.value;
    running = end;
    return { ...s, start, end, running };
  });

  const allVals = computed.flatMap((c) => [c.start, c.end]);
  const max = Math.max(...allVals, 0);
  const min = Math.min(...allVals, 0);
  const range = max - min || 1;

  const W = 760;
  const H = 280;
  const padL = 16;
  const padR = 16;
  const padT = 24;
  const padB = 56;
  const innerW = W - padL - padR;
  const innerH = H - padT - padB;
  const barW = (innerW / computed.length) * 0.62;
  const gap = (innerW / computed.length) * 0.38;

  const yFor = (v) => padT + (1 - (v - min) / range) * innerH;

  const colorFor = (kind, value) => {
    if (kind === 'start') return BRAND.verlo;
    if (kind === 'subtotal') return '#6366f1';
    if (kind === 'end') return value >= 0 ? '#10b981' : '#f43f5e';
    return value < 0 ? '#f43f5e' : '#94a3b8';
  };

  return (
    <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="xMidYMid meet" className="w-full h-full" data-testid="profit-waterfall-svg">
      {/* Zero line */}
      <line x1={padL} x2={W - padR} y1={yFor(0)} y2={yFor(0)} stroke="#e2e8f0" strokeDasharray="3 3" />
      <text x={padL} y={yFor(0) - 4} fill="#94a3b8" fontSize="10" fontFamily="Manrope, sans-serif">0</text>

      {computed.map((s, i) => {
        const xCenter = padL + (i + 0.5) * (innerW / computed.length);
        const x = xCenter - barW / 2;
        const top = yFor(Math.max(s.start, s.end));
        const bottom = yFor(Math.min(s.start, s.end));
        const height = Math.max(2, bottom - top);
        const color = colorFor(s.kind, s.value);
        const valueLabel = (s.kind === 'start' || s.kind === 'subtotal' || s.kind === 'end')
          ? fmtGBP(s.value)
          : (s.value < 0 ? '-' : '+') + fmtGBP(Math.abs(s.value));

        return (
          <g key={i} data-testid={`waterfall-step-${i}`}>
            <rect x={x} y={top} width={barW} height={height} rx="3" fill={color} opacity={0.9}>
              <title>{`${s.label}: ${valueLabel}`}</title>
            </rect>
            {/* Connector to next bar */}
            {i < computed.length - 1 && (
              <line
                x1={x + barW}
                x2={x + barW + gap}
                y1={yFor(s.running)}
                y2={yFor(s.running)}
                stroke="#cbd5e1"
                strokeDasharray="2 2"
              />
            )}
            {/* Value label above bar */}
            <text
              x={xCenter}
              y={top - 6}
              textAnchor="middle"
              fontSize="11"
              fontFamily="Manrope, sans-serif"
              fontWeight="500"
              fill="#0f172a"
            >
              {valueLabel}
            </text>
            {/* Category label below */}
            <text
              x={xCenter}
              y={H - padB + 18}
              textAnchor="middle"
              fontSize="10"
              fill="#64748b"
              fontWeight="600"
            >
              {s.label.toUpperCase()}
            </text>
          </g>
        );
      })}
    </svg>
  );
};

export default ProfitWaterfall;
